import { landmarkObstacleRings } from './landmarkObstacles';
import {
  damColors,
  materialsOf,
  masonryStyles,
  nieuweBrugMasonry,
  type Materials,
} from './landmarkMaterials';
import { dispatchBespoke } from './bespokeLandmarks';
import type { Ring, Vec2 } from '../../../geo/types';
import { scaleColor } from '../../../core/color';
import type { Landmark, PaletteModel } from '../../../data/types';
import type { Rng } from '../../../core/rng';
import type { MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import type { Obstacle } from '../../types';
import type { DetailSink } from '../detailSink';
import { edgeIdFor, SURFACE } from '../../mesh/attributes';
import { centroid } from '../../../geo/polygon';
import { painterPlaced } from './painterBaseLine';
import { readEnum, readNumber } from './params';
import { parseChurchParams } from './churchParams';
import { buildChurch } from './church';
import { buildTownhall, parseTownhallParams } from './townhall';
import { buildMonastery, parseMonasteryParams } from './monastery';
import { buildCrane } from './crane';
import { buildGallows } from './smallLandmarks';
import { buildNieuweBrugBuilding, NIEUWE_BRUG_BUILDINGS } from './nieuweBrugBuildings';
import { buildQuayTower } from './quayTower';
import { buildBoomFoundation } from './boomFoundation';
import { buildWaterfrontDefence } from './waterfrontDefences';
import { buildLongIndustrialShed, buildRopeWalk, PAINTED_ROPE_WALK_WALL } from './ropeWalk';
import { buildWaterGate } from './waterGate';
import { buildDam } from './dam';
import { buildMarketStalls } from './marketStalls';
import { buildTenterField } from './tenterField';
import {
  buildCourtyard,
  buildRadialBlock,
  buildRectBlock,
  WALL_NORMALS,
  type BlockSpec,
  type EntranceWall,
  type PlanRect,
} from './genericBlock';
import { weighhouseEntrance } from './weighhouse';
import { dominantAxis, meshFrameOf, planBounds, planFrameOf, type PlanFrame } from './planFrame';
import type { MasonryStyles } from './masonry';
import type { TracerySink } from './tracery/tracerySink';

export interface LandmarkBuildResult {
  readonly triangles: number;
  /** One per solid part that stands on the ground: a gate leaves its passage out of these. */
  readonly obstacles: readonly Obstacle[];
}

/** What every landmark is built against: the squares, the sink for window details, warnings. */
export interface LandmarkContext {
  /** Runtime stock is drawn separately so it can be packed away at closing time. */
  readonly marketGoods?: boolean;
  readonly marketOnDam?: boolean;
  readonly elevation?: number;
  readonly elevationAt?: (p: Vec2) => number;
  readonly squares: readonly Ring[];
  readonly details: DetailSink;
  readonly warn: (msg: string) => void;
}

/** What a civic block needs beyond its masonry to open its walls. */
interface CivicSetup {
  readonly context: LandmarkContext;
  readonly rng: Rng;
  readonly glass: VertexStyle;
  readonly leaf: VertexStyle;
  readonly edgeId: number;
  readonly entrance?: EntranceWall;
}

const DEG = Math.PI / 180;

export const LANDMARK_BLOCKS = {
  height: 8,
  pitch: 50 * DEG,
  radialSegments: { round: 16, octagonal: 8, square: 4 },
} as const;

/* The stalls' boards are darker than the posts the palette samples. */
const MARKET_BOARD_DARKEN = 0.7;

function blockSpec(landmark: Landmark, warn: (msg: string) => void): BlockSpec {
  const roof = readEnum(
    landmark.params,
    'roof',
    ['hip', 'flat', 'gable', 'cone'] as const,
    'hip',
    warn
  );
  return {
    eaveY: readNumber(landmark.params, 'height', LANDMARK_BLOCKS.height, warn),
    roof,
    pitch: LANDMARK_BLOCKS.pitch,
  };
}

/**
 * The wall of a block, counted as in `corners`, that faces the nearest square: a civic building
 * has its entrance toward the public space. Without squares it goes in a long wall.
 */
function doorWallOf(plan: PlanFrame, r: PlanRect, squares: readonly Ring[]): number {
  if (squares.length === 0) return r.u1 - r.u0 >= r.v1 - r.v0 ? 0 : 1;
  const c = Math.cos(plan.heading);
  const s = Math.sin(plan.heading);
  const centre: Vec2 = [(r.u0 + r.u1) / 2, (r.v0 + r.v1) / 2];
  const offsets = squares.map((ring): Vec2 => {
    const [x, y] = centroid(ring);
    const dx = x - plan.origin[0];
    const dy = y - plan.origin[1];
    return [dx * c + dy * s - centre[0], -dx * s + dy * c - centre[1]];
  });
  const toward = offsets.reduce((a, o) =>
    Math.hypot(o[0], o[1]) < Math.hypot(a[0], a[1]) ? o : a
  );
  const dots = WALL_NORMALS.map((n) => n[0] * toward[0] + n[1] * toward[1]);
  return dots.indexOf(Math.max(...dots));
}

/** Rectangular and courtyard blocks open every wall by the house rules; round and polygonal bodies are towers and stay closed. */
function buildBlockLandmark(
  builder: MeshBuilder,
  landmark: Landmark,
  styles: MasonryStyles,
  civic: CivicSetup
): void {
  const { warn, squares, details } = civic.context;
  const plan = planFrameOf(landmark.footprint, dominantAxis(landmark.footprint, landmark.heading));
  const frame = meshFrameOf(plan);
  const rect = planBounds(plan);
  const spec = blockSpec(landmark, warn);
  const shape = readEnum(
    landmark.params,
    'shape',
    ['rect', 'round', 'octagonal', 'square', 'courtyard'] as const,
    'rect',
    warn
  );
  if (shape === 'courtyard' || shape === 'rect') {
    const facade = {
      builder,
      frame,
      details,
      edgeId: civic.edgeId,
      styles: { wall: styles.wall, glass: civic.glass, leaf: civic.leaf },
    };
    const doorWall = doorWallOf(plan, rect, squares);
    const block = civic.entrance
      ? { facade, rng: civic.rng, doorWall, entrance: civic.entrance }
      : { facade, rng: civic.rng, doorWall };
    if (shape === 'courtyard') buildCourtyard(styles, block, rect, spec);
    else buildRectBlock(styles, block, rect, spec);
    return;
  }
  const radius = Math.min(rect.u1 - rect.u0, rect.v1 - rect.v0) / 2;
  const center: [number, number] = [(rect.u0 + rect.u1) / 2, (rect.v0 + rect.v1) / 2];
  const segments = LANDMARK_BLOCKS.radialSegments[shape];
  buildRadialBlock(
    builder,
    styles,
    frame,
    { center, radius, segments, phase: shape === 'square' ? Math.PI / 4 : 0 },
    spec
  );
}

/** Masonry buildings: churches and chapels, the town hall, monasteries and the civic blocks. */
function dispatchMasonry(
  builder: MeshBuilder,
  landmark: Landmark,
  m: Materials,
  ids: { edgeId: number; seed: number },
  context: LandmarkContext,
  openings: Rng
): void {
  const { warn } = context;
  const stone = { color: m.stone, surface: SURFACE.stone };
  const slate = { color: m.slate, surface: SURFACE.roofSlate };
  const tile = { color: m.tile, surface: SURFACE.roofTile };
  const infill = { ...ids, strokeAngle: 0, extent: [0, 0] as const };
  const glass: VertexStyle = { ...infill, color: m.glass, surface: SURFACE.linework };
  const leaf: VertexStyle = { ...infill, color: m.leaf, surface: SURFACE.wood };
  const civic: CivicSetup = { context, rng: openings, edgeId: ids.edgeId, glass, leaf };
  switch (landmark.kind) {
    case 'church':
    case 'chapel':
      buildChurch(
        builder,
        landmark,
        parseChurchParams(landmark, warn),
        { wall: m.stone, roof: m.churchSlate, opening: m.dark },
        ids.edgeId,
        ids.seed
      );
      return;
    case 'townhall': {
      const colors = {
        plaster: m.plaster,
        stone: m.stone,
        roof: m.slate,
        dark: m.dark,
        glass: m.glass,
        leaf: m.leaf,
      };
      const hall = { squares: context.squares, details: context.details, rng: openings };
      buildTownhall(builder, landmark, parseTownhallParams(landmark, warn), colors, hall, ids);
      return;
    }
    case 'monastery': {
      const styles = {
        church: { wall: m.stone, roof: m.churchSlate, opening: m.dark },
        ruin: masonryStyles(stone, slate, m, ids),
        /* The domestic ranges: plastered walls under the town's tiles, dark where they open. */
        range: masonryStyles(
          { color: m.plaster, surface: SURFACE.plaster },
          { color: m.tile, surface: SURFACE.roofTile },
          m,
          ids
        ),
        garth: m.garth,
      };
      buildMonastery(
        builder,
        landmark,
        parseMonasteryParams(landmark, warn),
        styles,
        ids.edgeId,
        ids.seed
      );
      return;
    }
    case 'waterGate': {
      buildWaterGate(
        builder,
        landmark,
        { wall: m.stone, roof: m.churchSlate, opening: m.dark },
        ids,
        warn,
        context.elevation ?? 0
      );
      return;
    }
    case 'dam': {
      buildDam(builder, landmark, damColors(m), ids, warn);
      return;
    }
    case 'weighhouse': {
      const brick = masonryStyles({ color: m.brick, surface: SURFACE.brick }, tile, m, ids);
      buildBlockLandmark(builder, landmark, brick, { ...civic, entrance: weighhouseEntrance });
      return;
    }
    case 'blockhouse':
      if (
        buildWaterfrontDefence(builder, landmark, {
          masonry: masonryStyles(
            stone,
            { color: m.churchSlate, surface: SURFACE.roofSlate },
            m,
            ids
          ),
          timber: { ...infill, color: m.timber, surface: SURFACE.wood },
        })
      )
        return;
      if (NIEUWE_BRUG_BUILDINGS.has(landmark.id)) {
        buildNieuweBrugBuilding(
          builder,
          landmark,
          {
            masonry: nieuweBrugMasonry(landmark.id, m, ids),
            timber: { ...infill, color: m.timber, surface: SURFACE.wood },
            tile: { ...infill, ...tile },
          },
          context.elevation ?? 0
        );
        return;
      }
      if (landmark.id === 'landmark-martelaars-quay-tower') {
        buildQuayTower(
          builder,
          landmark,
          masonryStyles(stone, { color: m.slate, surface: SURFACE.roofSlate }, m, ids),
          context.elevation ?? 0
        );
        return;
      }
      buildBoomFoundation(builder, landmark, { ...infill, color: m.timber, surface: SURFACE.wood });
      buildBlockLandmark(builder, landmark, masonryStyles(stone, tile, m, ids), civic);
      return;
    default:
      buildBlockLandmark(
        builder,
        landmark,
        masonryStyles({ color: m.plaster, surface: SURFACE.plaster }, tile, m, ids),
        civic
      );
  }
}

/** Timber structures: the crane, the gallows and the Lastage yards; false for a masonry kind. */
function dispatchTimber(
  builder: MeshBuilder,
  landmark: Landmark,
  m: Materials,
  ids: { edgeId: number; seed: number },
  rng: Rng,
  context: LandmarkContext
): boolean {
  const warn = context.warn;
  if (landmark.params['industrialLongShed'] === true) {
    buildLongIndustrialShed(
      builder,
      landmark,
      {
        wall: PAINTED_ROPE_WALK_WALL,
        roof: m.reed,
        opening: m.dark,
        lane: m.earth,
        timber: m.timber,
      },
      ids,
      warn
    );
    return true;
  }
  switch (landmark.kind) {
    case 'crane':
      buildCrane(builder, landmark, { timber: m.timber, rope: m.dark }, ids.edgeId, ids.seed, warn);
      return true;
    case 'gallows':
      buildGallows(
        builder,
        landmark,
        { timber: m.timber, earth: m.earth },
        ids.edgeId,
        ids.seed,
        warn
      );
      return true;
    case 'ropewalk':
      buildRopeWalk(
        builder,
        landmark,
        {
          wall: PAINTED_ROPE_WALK_WALL,
          roof: m.reed,
          opening: m.dark,
          lane: m.earth,
          timber: m.timber,
        },
        ids,
        warn
      );
      return true;
    case 'tenterField':
      buildTenterField(
        builder,
        landmark,
        { timber: m.timber, cloth: m.cloth, dyed: m.dyed },
        ids,
        rng.fork('cloth'),
        warn
      );
      return true;
    case 'marketStalls':
      buildMarketStalls(
        builder,
        landmark,
        {
          timber: m.timber,
          board: scaleColor(m.timber, MARKET_BOARD_DARKEN),
          ground: m.earth,
        },
        ids,
        warn,
        context.marketOnDam,
        context.marketGoods !== false
      );
      return true;
    default:
      return false;
  }
}

/** Raises one landmark; the churches hand their windows' tracery to `context.tracery` when it is given. */
export function buildLandmark(
  landmark: Landmark,
  builder: MeshBuilder,
  palette: PaletteModel,
  rng: Rng,
  context: LandmarkContext & { readonly edgeIdBase: number; readonly tracery?: TracerySink }
): LandmarkBuildResult {
  const ids = { edgeId: edgeIdFor(context.edgeIdBase), seed: rng.next() };
  const before = builder.triangleCount;
  const m = materialsOf(palette, rng);
  if (
    !dispatchBespoke(painterPlaced(builder, landmark.id), landmark, m, ids, context) &&
    !dispatchTimber(builder, landmark, m, ids, rng, context)
  ) {
    dispatchMasonry(builder, landmark, m, ids, context, rng.fork('openings'));
  }
  const polygons = landmarkObstacleRings(landmark);
  return {
    triangles: builder.triangleCount - before,
    obstacles: polygons.map((polygon): Obstacle => ({
      id: landmark.id,
      kind: 'landmark',
      polygon,
    })),
  };
}

export { collectReservedLandmarks } from './landmarkAccess';

export { buildAllLandmarks } from './landmarkBatch';
